import { Component, HostListener, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ArticleService } from 'src/app/services/article.service';
import { DiseaseService } from 'src/app/services/disease.service';
import { ErrorLogService } from 'src/app/services/error-log.service';
import { EXTERNAL_IDENTIFIER_DATABASES, EXTERNAL_IDENTIFIER_PREDICATE_MAPPINGS, EXTERNAL_RESOURCE_URLS } from 'shared/constants';
import { InfoSource } from 'shared/salesforce-types';
import { Section as SECTION, SubSection as SUBSECTION, Page as TAB, SubSubsection as SUBSUBSECTION } from 'shared/location-enums';
import { Article, ExternalIdentifier } from 'shared/types';

interface MoreInfoResource {
    article: Article;
    label: string;
    link: string;
}

interface MoreInfoResourceDefinition {
    readonly articleSubSubsection: SUBSUBSECTION;
    readonly getLink: (externalIdentifier: ExternalIdentifier) => string;
    readonly label: string;
    readonly matches: (externalIdentifier: ExternalIdentifier) => boolean;
}

const createExactUrlResourceDefinition = (label: string, database: string, articleSubSubsection: SUBSUBSECTION): MoreInfoResourceDefinition => ({
    articleSubSubsection,
    getLink: externalIdentifier => externalIdentifier.url?.trim() ?? '',
    label,
    matches: externalIdentifier => externalIdentifier.xrefDb === database && externalIdentifier.predicateMapping === EXTERNAL_IDENTIFIER_PREDICATE_MAPPINGS.exactMatch,
});

const MORE_INFO_RESOURCE_DEFINITIONS: readonly MoreInfoResourceDefinition[] = [
    createExactUrlResourceDefinition('Biomedical Data Translator', EXTERNAL_IDENTIFIER_DATABASES.biomedicalDataTranslator, SUBSUBSECTION.resourceBiomedicalDataTranslator),
    createExactUrlResourceDefinition('ClinGen', EXTERNAL_IDENTIFIER_DATABASES.clinGen, SUBSUBSECTION.resourceClinGen),
    {
        articleSubSubsection: SUBSUBSECTION.resourceGeneReviews,
        getLink: externalIdentifier => externalIdentifier.url?.trim() ?? '',
        label: 'GeneReview',
        matches: externalIdentifier => externalIdentifier.source === InfoSource.GeneReview,
    },
    {
        articleSubSubsection: SUBSUBSECTION.resourceMondo,
        getLink: externalIdentifier => {
            const mondoXref = externalIdentifier.xref?.trim();
            return mondoXref ? `${EXTERNAL_RESOURCE_URLS.monarchInitiative}${mondoXref}` : '';
        },
        label: 'MONDO',
        matches: externalIdentifier =>
            externalIdentifier.xrefDb === EXTERNAL_IDENTIFIER_DATABASES.mondo && externalIdentifier.predicateMapping === EXTERNAL_IDENTIFIER_PREDICATE_MAPPINGS.exactMatch,
    },
    createExactUrlResourceDefinition('Orphanet', EXTERNAL_IDENTIFIER_DATABASES.orphanet, SUBSUBSECTION.resourceOrphanet),
    createExactUrlResourceDefinition('RARe-SOURCE™', EXTERNAL_IDENTIFIER_DATABASES.rareSource, SUBSUBSECTION.resourceRareSource),
];

@Component({
    selector: 'app-disease-at-a-glance-summary',
    templateUrl: './disease-at-a-glance-summary.component.html',
    styleUrls: ['./disease-at-a-glance-summary.component.scss'],
})
export class DiseaseAtAGlanceSummaryComponent {
    private readonly desktopBreakpoint = 992;

    textHeader = '';
    public summaryText = '';
    public summaryMaxLines = 13;

    // More Info KAs
    infoTitle = '';
    moreInfoResources: MoreInfoResource[] = [];

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private diseaseService: DiseaseService,
        private articleService: ArticleService,
        private errorLogService: ErrorLogService,
    ) {
        this.setSummaryMaxLines();

        // check if this is the null version
        const isNull = DiseaseAtAGlanceSummaryComponent.isNull(diseaseService);

        // Title
        const headerArticle = articleService.getOne(TAB.about, SECTION.diseaseAtAGlance, SUBSECTION.summary, undefined, undefined, isNull);
        if (headerArticle) {
            this.textHeader = headerArticle.title;
        }

        // Summary
        // if we don't have description, load in the Null Article for summary section
        if (isNull) {
            this.summaryText = articleService.getOne(TAB.about, SECTION.diseaseAtAGlance, SUBSECTION.summary, undefined, undefined, isNull).bodyText;
        } else {
            this.summaryText = diseaseService.disease.clinicalDescription ?? '';
        }

        // More info KAs
        const infoParentKA = articleService.getOne(TAB.about, SECTION.diseaseAtAGlance, SUBSECTION.moreInfo, undefined, undefined, isNull, SUBSUBSECTION.parent);

        this.infoTitle = infoParentKA.title.replace('${DiseaseName}', this.diseaseService.disease.name);

        const externalIdentifiers = this.diseaseService.disease.externalIdentifiers ?? [];
        this.setMoreInfoResources(this.getMoreInfoResources(externalIdentifiers));
    }

    static isNull(diseaseService: DiseaseService): boolean {
        return !diseaseService.disease.clinicalDescription;
    }

    @HostListener('window:resize')
    onWindowResize() {
        this.setSummaryMaxLines();
    }

    private setSummaryMaxLines() {
        const width = this.document.defaultView?.innerWidth ?? this.desktopBreakpoint;
        this.summaryMaxLines = width >= this.desktopBreakpoint ? 13 : 8;
    }

    private getMoreInfoResources(externalIdentifiers: ExternalIdentifier[]): MoreInfoResource[] {
        return MORE_INFO_RESOURCE_DEFINITIONS.map(definition => {
            const matchingIdentifiers = externalIdentifiers.filter(definition.matches);
            if (matchingIdentifiers.length !== 1) {
                return undefined;
            }

            const link = definition.getLink(matchingIdentifiers[0]);
            if (!link) {
                return undefined;
            }

            return {
                article: this.articleService.getOne(TAB.about, SECTION.diseaseAtAGlance, SUBSECTION.moreInfo, undefined, undefined, false, definition.articleSubSubsection),
                label: definition.label,
                link,
            };
        }).filter((resource): resource is MoreInfoResource => !!resource);
    }

    private setMoreInfoResources(availableResources: MoreInfoResource[]): void {
        const orderCounts = new Map<number, number>();
        availableResources.forEach(resource => {
            if (Number.isFinite(resource.article.order)) {
                const order = resource.article.order as number;
                orderCounts.set(order, (orderCounts.get(order) ?? 0) + 1);
            }
        });

        const missingOrderResources = availableResources.filter(resource => !Number.isFinite(resource.article.order));
        const conflictingOrderResources = availableResources.filter(
            resource => Number.isFinite(resource.article.order) && (orderCounts.get(resource.article.order as number) ?? 0) > 1,
        );

        if (missingOrderResources.length || conflictingOrderResources.length) {
            const errors: string[] = [];

            if (missingOrderResources.length) {
                errors.push(`Missing Web Page Order: ${missingOrderResources.map(resource => resource.label).join(', ')}`);
            }

            if (conflictingOrderResources.length) {
                errors.push(`Conflicting Web Page Order: ${conflictingOrderResources.map(resource => `${resource.label} (${resource.article.order})`).join(', ')}`);
            }

            this.errorLogService.log('KA Order', `MoreInfo resources for ${this.diseaseService.disease.name}:\n${errors.join('\n')}`);
        }

        this.moreInfoResources = availableResources
            .filter(resource => Number.isFinite(resource.article.order) && orderCounts.get(resource.article.order as number) === 1)
            .sort((resourceA, resourceB) => (resourceA.article.order as number) - (resourceB.article.order as number));
    }
}
